import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency } from "@/lib/api";
import { Calculator, FileText, Download } from "lucide-react";

export default function TaxDepPage() {
  const { activeEntity, entities } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState(activeEntity?.id || "");

  useEffect(() => {
    if (activeEntity) setEntityFilter(activeEntity.id);
  }, [activeEntity]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (entityFilter) params.entity_id = entityFilter;
        const { data: d } = await api.get("/tax-depreciation", { params });
        setData(d);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [entityFilter]);

  const genJournal = async () => {
    if (!entityFilter) { alert("Select an entity first"); return; }
    if (!window.confirm("Generate deferred tax journal for this entity?")) return;
    try {
      const { data: j } = await api.post("/tax-depreciation/journal", { entity_id: entityFilter, period: new Date().toISOString().substring(0, 7) });
      alert(`Journal posted: ${j.reference || "No movement"}`);
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6" data-testid="tax-dep-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <Calculator size={24} className="inline mr-2 text-[#C8A961]" />
            Tax Depreciation & Deferred Tax
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">Parallel tax register with temporary differences and deferred tax computation (27% rate)</p>
        </div>
        <div className="flex gap-3">
          <select className="stratum-input" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} data-testid="tax-entity-filter">
            <option value="">All Entities</option>
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <button onClick={genJournal} className="stratum-btn-primary flex items-center gap-2" data-testid="gen-dt-journal-btn">
            <FileText size={14} /> Post DT Journal
          </button>
        </div>
      </div>

      {/* Totals */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stratum-card p-5">
            <span className="text-xs text-[#6B7280] uppercase tracking-wider">Accounting Dep (Annual)</span>
            <div className="font-mono text-xl font-semibold text-[#0B1020] tabular-nums mt-2">{formatCurrency(data.totals.total_acc_annual)}</div>
          </div>
          <div className="stratum-card p-5">
            <span className="text-xs text-[#6B7280] uppercase tracking-wider">Tax Dep (Annual)</span>
            <div className="font-mono text-xl font-semibold text-[#C8A961] tabular-nums mt-2">{formatCurrency(data.totals.total_tax_annual)}</div>
          </div>
          <div className="stratum-card p-5">
            <span className="text-xs text-[#6B7280] uppercase tracking-wider">Temporary Difference</span>
            <div className={`font-mono text-xl font-semibold tabular-nums mt-2 ${data.totals.total_temp_diff > 0 ? "text-[#991B1B]" : "text-[#065F46]"}`}>
              {formatCurrency(Math.abs(data.totals.total_temp_diff))} {data.totals.total_temp_diff > 0 ? "(DTL)" : data.totals.total_temp_diff < 0 ? "(DTA)" : ""}
            </div>
          </div>
          <div className="stratum-card p-5">
            <span className="text-xs text-[#6B7280] uppercase tracking-wider">Deferred Tax @ {data.totals.tax_rate}%</span>
            <div className="font-mono text-xl font-semibold text-[#0B1020] tabular-nums mt-2">{formatCurrency(Math.abs(data.totals.total_deferred_tax))}</div>
          </div>
        </div>
      )}

      {/* Register Table */}
      {loading ? (
        <div className="stratum-card p-8 text-center text-[#6B7280]">Loading...</div>
      ) : !data || data.register.length === 0 ? (
        <div className="stratum-card p-12 text-center text-[#6B7280]">No assets to show. Import or add assets first.</div>
      ) : (
        <div className="stratum-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="tax-dep-table">
              <thead>
                <tr className="bg-[#0B1020] text-white">
                  <th className="px-2 py-2 text-left" rowSpan={2}>Asset</th>
                  <th className="px-2 py-2 text-right" rowSpan={2}>Cost</th>
                  <th className="px-2 py-1.5 text-center border-l border-white/20" colSpan={3}>Accounting</th>
                  <th className="px-2 py-1.5 text-center border-l border-white/20" colSpan={3}>Tax (SARS)</th>
                  <th className="px-2 py-1.5 text-center border-l border-white/20" colSpan={2}>Deferred Tax</th>
                </tr>
                <tr className="bg-[#1A233A] text-white/70 text-[10px] uppercase tracking-wider">
                  <th className="px-2 py-1 text-right border-l border-white/10">Annual</th>
                  <th className="px-2 py-1 text-right">Accum</th>
                  <th className="px-2 py-1 text-right">Carrying</th>
                  <th className="px-2 py-1 text-right border-l border-white/10">Annual</th>
                  <th className="px-2 py-1 text-right">Accum</th>
                  <th className="px-2 py-1 text-right">Carrying</th>
                  <th className="px-2 py-1 text-right border-l border-white/10">Temp Diff</th>
                  <th className="px-2 py-1 text-right">DT @ 27%</th>
                </tr>
              </thead>
              <tbody>
                {data.register.map((r, i) => (
                  <tr key={r.asset_id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAF8F4]"}>
                    <td className="px-2 py-1.5">
                      <div className="font-mono font-medium">{r.tag}</div>
                      <div className="text-[#6B7280] truncate max-w-[150px]">{r.description}</div>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums">{formatCurrency(r.cost)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums border-l border-[#E8E2D5]">{formatCurrency(r.acc_annual)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-[#991B1B]">({formatCurrency(r.acc_accum)})</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums font-medium">{formatCurrency(r.acc_carrying)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums border-l border-[#E8E2D5]">{formatCurrency(r.tax_annual)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-[#991B1B]">({formatCurrency(r.tax_accum)})</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums font-medium">{formatCurrency(r.tax_carrying)}</td>
                    <td className={`px-2 py-1.5 text-right font-mono tabular-nums border-l border-[#E8E2D5] ${r.temporary_difference > 0 ? "text-[#991B1B]" : r.temporary_difference < 0 ? "text-[#065F46]" : ""}`}>
                      {formatCurrency(r.temporary_difference)}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums font-medium">{formatCurrency(r.deferred_tax)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#0B1020] text-white font-semibold text-xs">
                  <td className="px-2 py-2">Total</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums">{formatCurrency(data.register.reduce((s, r) => s + r.cost, 0))}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums border-l border-white/20">{formatCurrency(data.totals.total_acc_annual)}</td>
                  <td className="px-2 py-2" colSpan={2}></td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums border-l border-white/20">{formatCurrency(data.totals.total_tax_annual)}</td>
                  <td className="px-2 py-2" colSpan={2}></td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-[#C8A961] border-l border-white/20">{formatCurrency(data.totals.total_temp_diff)}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-[#C8A961]">{formatCurrency(data.totals.total_deferred_tax)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
